import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { isGameDone, setGameDone } from "../apiHandles/user_progress.js";
import "../assets/css/colors.css";
import { useDarkMode } from 'context/DarkModeProvider.js';
import { useLanguage } from 'context/LanguageProvider.js';
import "../assets/css/colors.css"
import { increaseSubStructure, increaseStructure } from '../apiHandles/user_progress.js';
import axiosInstance from 'apiHandles/axiosInstance';
import CryptoJS from 'crypto-js';


const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY;

function decryptData(encryptedData) {
    try {
        const key = CryptoJS.SHA256(SECRET_KEY);

        const decrypted = CryptoJS.AES.decrypt(
            encryptedData,
            key,
            {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedString);
    } catch (error) {
        console.error("Decryption failed:", error);
        return null;
    }
}


const dataPercentage = {
    "Directed": [12.5, 7.1],
    "Undirected": [16.6, 7.1],
    "Binary Tree": [12.5, 4.1],
    "AVL Tree": [12.5, 4.1],
    "Heap": [12.5, 4.1],
    "Singly Linked List": [12.5, 6.25],
    "Doubly Linked List": [12.5, 6.25],
    "1D Array": [12.5, 4.1],
    "2D Array": [10, 4.1],
    "Dynamic Array": [16.6, 4.1],
    "Chaining": [12.5, 7.1],
    "Open Addressing": [16.6, 7.1],
    "Circular Queue": [12.5, 7.1],
    "Priority Queue": [16.6, 7.1],
    "Array-based Stack": [16.6, 10.0],
    "Linked List Stack": [25.0, 10.0],
    "Hash Set": [16.6, 8.3],
    "Tree Set": [16.6, 8.3],
};

export default function GamesPage() {
    const navigate = useNavigate();

    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [checkingGameStatus, setCheckingGameStatus] = useState(true);
    const [gameAlreadyDone, setGameAlreadyDone] = useState(false);
    const [gameData, setGameData] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [firstTry, setFirstTry] = useState(false);
    const [continueNavigation, setContinueNavigation] = useState(null);
    const [gameExists, setGameExists] = useState(true);
    const [loadingStage, setLoadingStage] = useState(true); // ✅ Πρόσθεσε
    const [selectedStage, setSelectedStage] = useState(null); // ✅ Πρόσθεσε

    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const handleCheckGameDone = async (stageId) => {
        try {
            const gameDone = await isGameDone(stageId);
            localStorage.setItem("gameDone", gameDone);
            setGameAlreadyDone(gameDone);
        } catch (error) {
            console.error("Error checking game status:", error);
            setGameAlreadyDone(false);
        } finally {
            setCheckingGameStatus(false);
            setShowWelcomeModal(true);
        }
    };

    useEffect(() => {
        const loadStageData = async () => {
            try {
                setLoadingStage(true);

                const stageId = localStorage.getItem("selectedStageId");

                if (!stageId) {
                    console.error("No stage ID found in localStorage");
                    setGameExists(false);
                    setCheckingGameStatus(false);
                    setLoadingStage(false);
                    return;
                }

                // ✅ Φόρτωσε ΜΟΝΟ τα games
                const response = await axiosInstance.get(`/api/stage/${stageId}/games/`);

                // ✅ Πάρε το encrypted data από το wrapper
                const encryptedData = response.data.data;

                if (!encryptedData) {
                    console.error("No encrypted data received");
                    setGameExists(false);
                    setCheckingGameStatus(false);
                    setLoadingStage(false);
                    return;
                }

                // ✅ Αποκρυπτογράφησε
                const data = decryptData(encryptedData);

                if (!data) {
                    console.error("Failed to decrypt data");
                    setGameExists(false);
                    setCheckingGameStatus(false);
                    setLoadingStage(false);
                    return;
                }

                // ✅ Έλεγξε αν υπάρχουν games
                if (!data.games || data.games.length === 0) {
                    console.error("No games available");
                    setGameExists(false);
                    setCheckingGameStatus(false);
                    setLoadingStage(false);
                    return;
                }

                // ✅ Κράτα μόνο τα απαραίτητα
                setSelectedStage({
                    id: data.id,
                    parent_sub_structure: data.parent_sub_structure
                });

                const game = data.games[0];

                setGameData({
                    game,
                    selectedStage: {
                        id: data.id,
                        parent_sub_structure: data.parent_sub_structure
                    },
                    selectedSubCategory: JSON.parse(localStorage.getItem("SubCategoryClicked"))
                });

                handleCheckGameDone(data.id);

            } catch (error) {
                console.error("Failed to load games:", error);
                setGameExists(false);
                setCheckingGameStatus(false);
            } finally {
                setLoadingStage(false);
            }
        };

        loadStageData();
    }, [navigate]);

    const handleStartGame = () => {
        if (!gameData) return;

        const { game, selectedStage, selectedSubCategory } = gameData;

        localStorage.setItem("gameClicked", game.game_title);
        localStorage.setItem("gameFirstTry", (!gameAlreadyDone).toString());

        if (selectedSubCategory && selectedStage) {
            if (!gameAlreadyDone) {
                setGameDone(selectedStage.id);
                setFirstTry(true);
                const subStructure = selectedSubCategory.name;
                const values = dataPercentage[subStructure];

                if (values && Array.isArray(values) && values.length >= 2) {
                    const precSubStructure = values[0];
                    increaseSubStructure(precSubStructure, selectedStage.id);
                    increaseStructure(values[1], selectedStage.id, selectedStage.parent_sub_structure);
                } else {
                    console.error(`No data percentage found for subStructure: ${subStructure}`);
                }
            }

            setIsNavigating(true);
            navigate(`/gameFiled/${selectedSubCategory.name}/${game.id}`);
        } else {
            console.error("Missing stage or subcategory data");
            alert("Error: Missing game data. Please try again.");
        }
    };

    const handleGameFinish = () => {
        setIsFinished(true);
        setFirstTry(false);
    };


    if (loadingStage || checkingGameStatus) {
        return (
            <div style={{
                background: darkMode ? "#0f172a" : "var(--lightModeBack)",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px"
            }}>
                <div style={{
                    textAlign: "center"
                }}>
                    <div style={{
                        width: "60px",
                        height: "60px",
                        border: `4px solid ${darkMode ? "#1e293b" : "#cbd5e1"}`,
                        borderTop: "4px solid #3b82f6",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 30px"
                    }} />

                    <h1 style={{
                        fontSize: "2rem",
                        fontWeight: "600",
                        color: darkMode ? "#f1f5f9" : "#1e293b",
                        marginBottom: "15px"
                    }}>
                        {selLanguage ? "Φόρτωση..." : "Loading..."}
                    </h1>

                    <p style={{
                        color: darkMode ? "#94a3b8" : "#64748b",
                        fontSize: "1.1rem"
                    }}>
                        {selLanguage
                            ? "Παρακαλώ περιμένετε..."
                            : "Please wait while we prepare your gaming experience"
                        }
                    </p>
                </div>

                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Έλεγχος αν δεν υπάρχει παιχνίδι
    if (!gameExists) {
        return (
            <div style={{
                background: darkMode ? "#0f172a" : "var(--lightModeBack)",
                minHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px"
            }}>
                <div style={{
                    textAlign: "center",
                    maxWidth: "600px"
                }}>
                    <p style={{
                        color: darkMode ? "#f7f7f7ff" : "#1e293b",
                        fontSize: "20px",
                        lineHeight: "1.8",
                        marginBottom: "40px",
                        fontWeight: "bold"
                    }}>
                        {selLanguage ? "Αυτό το παιχνίδι δεν είναι διαθέσιμο ακόμη." : "This game is not available yet."}
                    </p>

                    <Button
                        onClick={() => navigate('/admin/index')}
                        style={{
                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "0.75rem",
                            padding: "1rem 2.5rem",
                            fontSize: "1.1rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            boxShadow: "0 2px 10px rgba(59, 130, 246, 0.2)"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                        }}
                    >
                        {selLanguage ? "Επιστροφή στην Αρχική" : "Back to main"}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            background: darkMode ? "#0f172a" : "var(--lightModeBack)",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px"
        }}>
            {/* Welcome Modal */}
            <Modal isOpen={showWelcomeModal} centered contentClassName="no-shadow-modal">
                <div style={{
                    background: darkMode ? "#1e293b" : "var(--lightModeModal)",
                    borderRadius: '0.5rem',
                    maxWidth: "90vw",
                }}>
                    <ModalHeader
                        style={{
                            fontSize: "clamp(1.2rem, 4vw, 1.5rem)",
                            textAlign: "center",
                            color: "#cf3838ff",
                            justifyContent: "center"
                        }}>
                        {selLanguage ? "Προειδοποίηση!" : "Warning!"}
                    </ModalHeader>

                    <ModalBody style={{ padding: "clamp(1rem, 3vw, 2rem)" }}>
                        <p style={{
                            textAlign: "center",
                            color: darkMode ? "#e4e4e4ff" : "#1b1b1bff",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center"
                        }}>
                            {gameAlreadyDone
                                ? (
                                    <>
                                        {selLanguage ? (
                                            <>
                                                Έχεις ήδη ολοκληρώσει αυτό το παιχνίδι.<br />
                                                Δεν θα πάρεις ξανά XP, αλλά μπορείς να το παίξεις για εξάσκηση!
                                            </>
                                        ) : (
                                            <>
                                                You have already completed this game.<br />
                                                You won't earn XP again, but you can play it for practice!
                                            </>
                                        )}
                                    </>
                                )
                                : (
                                    <>
                                        {selLanguage ? (
                                            <>
                                                Το παιχνίδι παρέχει XP μόνο στην πρώτη ολοκλήρωση.<br />
                                                Μπορείτε να το επαναλάβετε οποιαδήποτε στιγμή για να τεστάρετε τις γνώσεις σας, αλλά δεν θα λάβετε επιπλέον XP :)
                                            </>
                                        ) : (
                                            <>
                                                You will earn XP only once for this game.<br />
                                                You can retake it for practice, but no further XP will be awarded :)
                                            </>
                                        )}
                                    </>
                                )
                            }
                        </p>
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "1rem",
                            flexWrap: "wrap"
                        }}>
                            <Button
                                onClick={() => navigate(`/admin/index`)}
                                style={{
                                    background: "linear-gradient(135deg, #c73434ff 0%, #960909ff 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "0.5rem",
                                    padding: "0.75rem 1.5rem",
                                    fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                                    minWidth: "100px"
                                }}
                            >
                                {selLanguage ? "Πίσω" : "Back"}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowWelcomeModal(false);
                                    handleStartGame();
                                }}
                                style={{
                                    borderRadius: "0.5rem",
                                    padding: "0.75rem 1.5rem",
                                    fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                                    minWidth: "100px",
                                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                    color: "#fff",
                                    border: "none"
                                }}
                            >
                                {selLanguage ? "Έναρξη" : "Start"}
                            </Button>
                        </div>
                    </ModalBody>
                </div>
            </Modal>

            {/* Loading Game Content */}
            {!showWelcomeModal && (
                <div style={{
                    textAlign: "center"
                }}>
                    {gameExists && (
                        <div style={{
                            width: "60px",
                            height: "60px",
                            border: `4px solid ${darkMode ? "#1e293b" : "#cbd5e1"}`,
                            borderTop: "4px solid #3b82f6",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto 30px"
                        }} />
                    )}

                    <h1 style={{
                        fontSize: "2rem",
                        fontWeight: "600",
                        color: darkMode ? "#f1f5f9" : "#1e293b",
                        marginBottom: "15px"
                    }}>
                        {selLanguage ? "Φόρτωση παιχνιδιού..." : "Loading Game..."}
                    </h1>

                    <p style={{
                        color: darkMode ? "#94a3b8" : "#64748b",
                        fontSize: "1.1rem"
                    }}>
                        {selLanguage
                            ? "Παρακαλώ περιμένετε..."
                            : "Please wait while we prepare your gaming experience"
                        }
                    </p>
                </div>
            )}

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}